import React, {useEffect} from 'react';
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
    Alert, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import AddEventWidget from "./AddEventWidget";
import Button from "@mui/material/Button";
import {useCreateEventsMutation, useGetCompEventsQuery} from "../../slices/apiSlice";
import {SGP} from "../Competition/sgp-results-react";
import moment from "moment-timezone";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import {getEventDatesString} from "./index";
import TableContainer from "@mui/material/TableContainer";
import {styled} from "@mui/material/styles";
import TableCell, {tableCellClasses} from "@mui/material/TableCell";
import {grey} from "@mui/material/colors";
import {Download} from "@mui/icons-material";
import {countryArray} from "../../util/standards-util";
import SeriesSelector from "../../components/Event/SeriesSelector";

export const seasonSetupConfig = {
    minEvents: 6,
    avgEvents: 10,
    maxEvents: 14,
    defaultPracticeDays: 1,
    defaultCompetitionDays: 7,
}

const suffixedNumber = (num) => {
    const j = num % 10,
        k = num % 100;
    if (j === 1 && k !== 11) {
        return `${num}st`;
    } else if (j === 2 && k !== 12) {
        return `${num}nd`;
    } else if (j === 3 && k !== 13) {
        return `${num}rd`;
    } else {
        return `${num}th`;
    }
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: '.85em',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: grey[100],
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const EventDetailsTable = ({data}) => {

    const [fileBody, setFileBody] = React.useState()

    useEffect(()=>{
        if (data.status === 'ok') {
            const eventReqs = data.data

            let txt = `"#","Event","Manager username","Manager password","Registration link"\r\n`

            for (let i=0; i<eventReqs.length ; i++) {
                const evt = eventReqs[i].event
                const rules = eventReqs[i].rules

                txt+=`${evt.edition},"${evt.fullEditionTitle}","${rules.username}","${rules.password}","${window.location.href.substring(0,window.location.href.lastIndexOf(window.location.pathname))}/eventRegistration/${evt.id}"\r\n`
            }

            setFileBody(txt)
        }
    },[])

    const handleDownload = () => {
        const blob = new Blob([fileBody], { type: "text/csv" })
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `sgp-series-${data.data[0].event.series}-event-info.csv`
        link.href = url;
        link.click();
    }

        return (
            <Box>
                {data.status === "ok" &&
                    <>
                        <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',mb:'1em'}}>
                            <Typography variant={'h3'}>Series created successfully</Typography>
                            <Button
                                onClick={handleDownload}
                                size = 'small'
                                endIcon={<Download/>}
                                >
                                Download event details CSV file
                            </Button>
                        </Box>
                        <Box sx={{textAlign:'left',mb:'2em'}}>
                            <Typography variant={'body2'} sx={{fontStyle:'italic'}}>Please note the user & password information or download the CSV file.
                                This file can be opened using excel or any other compatible application.
                                This is the only time the password for the event manager users will be displayed.
                                If needed, an event manager password can be reset from the individual event page.
                            </Typography>
                        </Box>
                        <TableContainer component={'div'} sx={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>#</StyledTableCell>
                                        <StyledTableCell>Event</StyledTableCell>
                                        <StyledTableCell>Manager username</StyledTableCell>
                                        <StyledTableCell>Manager password</StyledTableCell>
                                        <StyledTableCell>Registration link</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.data.map((req, i) => {
                                        const evt = req.event
                                        const rules = req.rules

                                        return (
                                            <StyledTableRow
                                                key={evt.id}
                                                sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                            >
                                                <StyledTableCell component="th" scope="row">{evt.edition}</StyledTableCell>
                                                <StyledTableCell>{evt.fullEditionTitle}</StyledTableCell>
                                                <StyledTableCell>{rules.username}</StyledTableCell>
                                                <StyledTableCell>{rules.password}</StyledTableCell>
                                                <StyledTableCell>{window.location.href.substring(0,window.location.href.lastIndexOf(window.location.pathname))}/eventRegistration/{evt.id}</StyledTableCell>
                                            </StyledTableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                }
                { data.status === "error" &&
                    <Alert severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                        There was an error during the season creation: {data.error}
                    </Alert>
                }
            </Box>

        )
}

const CreateConfirmDialog = ({events, onClose}) => {

    const [open, setOpen] = React.useState(true)

    const handleCancel = () => {
        setOpen(false)
        onClose();
    }

    const handleOk = () => {
        setOpen(false)
        onClose(true);
    }

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
            maxWidth="md"
            open={open}
        >
            <DialogTitle>Please confirm series creation</DialogTitle>
            <DialogContent>
                <TableContainer component={'div'} sx={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>#</StyledTableCell>
                                <StyledTableCell>Event</StyledTableCell>
                                <StyledTableCell>Venue</StyledTableCell>
                                <StyledTableCell>Dates</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((evt) => (
                                <StyledTableRow
                                    key={evt.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <StyledTableCell component="th" scope="row">
                                        {evt.edition}
                                    </StyledTableCell>
                                    <StyledTableCell>{evt.fullEditionTitle}</StyledTableCell>
                                    <StyledTableCell>{evt.venue}</StyledTableCell>
                                    <StyledTableCell>{getEventDatesString(evt)}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleOk}>Create</Button>
            </DialogActions>
        </Dialog>
    )
}

const AddEvent = ({onClose, seasonMode = true}) => {

    let k=0

    const [eventData, setEventData] = React.useState((()=>{
        const d = []
        for (let i=0 ; i<seasonSetupConfig.maxEvents ; i++) {
            d.push({
                handicapped: false,
                practiceDays: seasonSetupConfig.defaultPracticeDays,
                competitionDays: seasonSetupConfig.defaultCompetitionDays,
            })
        }
        return d
    })())
    const [mode, setMode] = React.useState(seasonMode ? 'season' : 'event');
    const [eventNo, setEventNo] = React.useState(seasonSetupConfig.avgEvents);
    const [eventWidgets, setEventWidgets] = React.useState()
    const [series, setSeries] = React.useState()
    const [singleEventSeries, setSingleEventSeries] = React.useState()
    const [validationErrors,setValidationErrors] = React.useState([])
    const [createEventObjects,setCreateEventObjects] = React.useState()
    const [createRequest,setCreateRequest] = React.useState()

    const {data: comps, isSuccess: isCompEventSuccess} =
        useGetCompEventsQuery({compType:3})

    const [createEvents, createEventsResult] =
        useCreateEventsMutation()

    const updateEventData = (n , key, value) => {
        eventData[n][key]=value
    }

    const handleModeChange = (event, newMode) => {
        setMode(newMode);
    };

    const handleEventNoChanged = (event, newValue) => {
        setEventNo(newValue)
    }

    const handleCreateConfirmDialogClose = (confirmCreate) => {
        if (confirmCreate) {
            console.log(createRequest)
            createEvents(createRequest)
            setMode("createSeries")
        }
    }

    const handleCreateEventFinishedClick = () => {
        onClose()
    }

    const handleSeriesChange = (s) => {
        setSingleEventSeries(s)
    }

    const createSeason = () =>{
        let cnt = 0
        const alerts=[]
        const req = []
        const events = []
        const countriesUsed = {}

        for (let i=0 ; i < eventNo ; i++) {
            const d = eventData[i]
            const evt =  {}
            const rule = {}
            req.push({
                event: evt,
                rules: rule,
            })
            events.push(evt)

            if ( i+1 < eventNo && d.country !== undefined && countriesUsed[d.country]!==undefined) {
                alerts.push(
                    <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                        Event #{i+1} has country that is already used in qualifying event #{countriesUsed[d.country]+1}
                    </Alert>
                )
            }
            else {
                countriesUsed[d.country] = i
            }

            if (d.country === undefined)
                alerts.push(
                    <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                        Event #{i+1} has no country set
                    </Alert>
                )
            else {
                const country = countryArray.find( c => c.key === d.country).label

                const startDate = moment(d.startDate)

                evt['@type']='CompetitionEdition'
                evt.competitionId = 3
                evt.edition = i+1
                evt.editionTitle = i+1 === eventNo ? 'SGP Final' :   `${country} SGP`
                evt.fullEditionTitle =  i+1 === eventNo ? `${suffixedNumber(series)} SGP World Final` : `${country} SGP ${startDate.format('YYYY')}`
                evt.venue = d.venue
                evt.country = d.country
                evt.firstDate = moment(startDate).subtract(d.practiceDays,'day').format('YYYY-MM-DD')
                evt.firstRacingDate = d.startDate
                evt.lastDate = moment(startDate).add(d.competitionDays-1,'day').format('YYYY-MM-DD')
                evt.handicapListId = 0
                //TODO: add rulespace control - for now we correlate this with the series
                evt.ruleSpaceId = series
                evt.series = series
                evt.status = 30
                evt.isFinal = i+1 === eventNo

                rule.airfield = d.airfield
                rule.handicapped = d.handicapped
                rule.practiceDays = d.practiceDays
                rule.competitionDays = d.competitionDays
                rule.country = d.country
                rule.maxCompetitors = 20
                rule.reservedForeignBerths = evt.isFinal ? 0 : 5
                rule.wildcards = 1
                rule.isFinal = false
            }

        }

        if (alerts.length === 0) {
            //show confirmation screen
            setCreateEventObjects(events)
            setCreateRequest(req)
            setMode("createConfirm")
        }

        setValidationErrors(alerts)
    }

    const createSingleEvent = () =>{
        let cnt = 0
        const alerts=[]
        const req = []
        const events = []
        //TODO: we do not check for countries use in series, so this could be a problem
        const countriesUsed = {}

        const d = eventData[0]
        const evt =  {}
        const rule = {}
        req.push({
            event: evt,
            rules: rule,
        })
        events.push(evt)

        /*
        if ( i+1 < eventNo && d.country !== undefined && countriesUsed[d.country]!==undefined) {
            alerts.push(
                <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                    Event #{i+1} has country that is already used in qualifying event #{countriesUsed[d.country]+1}
                </Alert>
            )
        }
        else {
            countriesUsed[d.country] = i
        }
        */

        if (d.country === undefined)
            alerts.push(
                <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                    Event has no country set
                </Alert>
            )
        else {
            const country = countryArray.find( c => c.key === d.country).label

            const startDate = moment(d.startDate)

            evt['@type']='CompetitionEdition'
            evt.competitionId = 3
            evt.edition = 0
            evt.editionTitle = `${country} SGP`
            evt.fullEditionTitle =  `${country} SGP ${startDate.format('YYYY')}`
            evt.venue = d.venue
            evt.country = d.country
            evt.firstDate = moment(startDate).subtract(d.practiceDays,'day').format('YYYY-MM-DD')
            evt.firstRacingDate = d.startDate
            evt.lastDate = moment(startDate).add(d.competitionDays-1,'day').format('YYYY-MM-DD')
            evt.handicapListId = 0
            //TODO: add rulespace control - for now we correlate this with the series
            //TODO: we need to handle this in a special way, of this is an off-series event
            evt.ruleSpaceId = singleEventSeries
            evt.series = singleEventSeries
            evt.status = 30
            evt.isFinal = false

            rule.airfield = d.airfield
            rule.handicapped = d.handicapped
            rule.practiceDays = d.practiceDays
            rule.competitionDays = d.competitionDays
            rule.country = d.country
            rule.maxCompetitors = 20
            rule.reservedForeignBerths = evt.isFinal ? 0 : 5
            rule.wildcards = 1
            rule.isFinal = false
        }



        if (alerts.length === 0) {
            //show confirmation screen
            setCreateEventObjects(events)
            setCreateRequest(req)
            setMode("createConfirm")
        }

        setValidationErrors(alerts)
    }

    const createNonSeriesEvent = () =>{
        let cnt = 0
        const alerts=[]
        const req = []
        const events = []

        const d = eventData[0]
        const evt =  {}
        const rule = {}
        req.push({
            event: evt,
            rules: rule,
        })
        events.push(evt)

        /*
        if ( i+1 < eventNo && d.country !== undefined && countriesUsed[d.country]!==undefined) {
            alerts.push(
                <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                    Event #{i+1} has country that is already used in qualifying event #{countriesUsed[d.country]+1}
                </Alert>
            )
        }
        else {
            countriesUsed[d.country] = i
        }
        */

        if (d.country === undefined)
            alerts.push(
                <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                    Event has no country set
                </Alert>
            )
        else if (d.fullEditionTitle === undefined)
            alerts.push(
                <Alert key={++cnt} severity="error" sx={{margin: '0.5em 0 1em 0'}}>
                    Event has no title
                </Alert>
            )
        else {
            const country = countryArray.find( c => c.key === d.country).label

            const startDate = moment(d.startDate)

            evt['@type']='CompetitionEdition'
            evt.competitionId = 3
            evt.edition = 1
            evt.editionTitle = d.editionTitle ? d.editionTitle : d.fullEditionTitle
            evt.fullEditionTitle =  d.fullEditionTitle
            evt.venue = d.venue
            evt.country = d.country
            evt.firstDate = moment(startDate).subtract(d.practiceDays,'day').format('YYYY-MM-DD')
            evt.firstRacingDate = d.startDate
            evt.lastDate = moment(startDate).add(d.competitionDays-1,'day').format('YYYY-MM-DD')
            evt.handicapListId = 0
            //TODO: add rulespace control - for now we correlate this with the series
            //TODO: we need to handle this in a special way, of this is an off-series event
            evt.ruleSpaceId = singleEventSeries
            evt.series = 0
            evt.status = 30
            evt.isFinal = false

            rule.airfield = d.airfield
            rule.handicapped = d.handicapped
            rule.practiceDays = d.practiceDays
            rule.competitionDays = d.competitionDays
            rule.country = d.country
            rule.maxCompetitors = 20
            rule.reservedForeignBerths = evt.isFinal ? 0 : 5
            rule.wildcards = 1
            rule.isFinal = false
        }



        if (alerts.length === 0) {
            //show confirmation screen
            setCreateEventObjects(events)
            setCreateRequest(req)
            setMode("createConfirm")
        }

        setValidationErrors(alerts)
    }

    useEffect(()=>{
        if (createEventsResult.isSuccess) {
            console.log('*** created events successfuilly')
            console.log(createEventsResult.data)
        }
    },[createEventsResult])

    useEffect(()=>{
        const r = []

        for (let i=0 ; i<eventNo ; i++ ) {
            r.push(<AddEventWidget key={i} no={i} isFinal={i === eventNo-1} updateEventData={updateEventData}/>)
        }
        setEventWidgets(r)
    },[eventNo])

    useEffect(()=>{
        if (isCompEventSuccess){
            const s = Array.from((new Set(comps.map(c => c.series)))).sort((a,b) => {return  b - a})
            setSeries(s[0]+1)
            setSingleEventSeries(s[0])
        }
    },[isCompEventSuccess])

    return (
        <Container maxWidth={'lg'}>
            <Box sx={{
                display:'flex',
                alignItems:'center',
                mt:'2em',
                mb:'1em',
            }}>
                { mode=='season' && <Typography variant={'h2'} flexGrow={1} sx={{textAlign:'left'}}>Create a new series</Typography>}
                { mode=='event' && <Typography variant={'h2'} flexGrow={1} sx={{textAlign:'left'}}>Add event to series</Typography>}
                { mode=='nonSeries' && <Typography variant={'h2'} flexGrow={1} sx={{textAlign:'left'}}>Create non-series event</Typography>}
                {
                <ToggleButtonGroup
                    size='small'
                    color="primary"
                    value={mode}
                    exclusive
                    onChange={handleModeChange}
                >
                    <ToggleButton value="season">Create new series</ToggleButton>
                    <ToggleButton value="event">Add event to series</ToggleButton>
                    <ToggleButton value="nonSeries">Create non-series event</ToggleButton>
                </ToggleButtonGroup>
                }
            </Box>

            { mode === 'season' &&
                <Box mt={4}>
                    <Stack direction={'row'} alignItems={'center'} spacing={2}>
                        <Box sx={{textAlign:'left'}} flexGrow={1}>
                            {series &&
                                <Typography variant={'h3'}>{SGP.getChampionshipTitle({series: series})}</Typography>
                            }
                        </Box>
                        <Typography variant={'body1'}>Events in season</Typography>
                        <Slider
                            aria-label="Events in season"
                            defaultValue={seasonSetupConfig.avgEvents}
                            step={1}
                            marks
                            min={seasonSetupConfig.minEvents}
                            max={seasonSetupConfig.maxEvents}
                            onChange={handleEventNoChanged}
                            sx={{width:'20em'}}
                        />
                        <Typography variant={'body1'} ml={4} sx={{width:'3em'}}>{eventNo}</Typography>
                    </Stack>


                    <Stack mt={2}>
                        {eventWidgets}
                    </Stack>

                    { validationErrors.length > 0 &&
                        <Box mt={3}>
                            {validationErrors}
                        </Box>
                    }

                    <Box my={4}>
                        <Button variant={'contained'} onClick={createSeason} fullWidth>Create season</Button>
                    </Box>

                </Box>
            }
            { mode === 'event' &&
                <Box mt={4}>

                    <Stack mt={2}>
                        <SeriesSelector onChange={handleSeriesChange}/>
                        <AddEventWidget key={0} no={0} isFinal={false} updateEventData={updateEventData} singleMode={true}/>
                    </Stack>

                    { validationErrors.length > 0 &&
                        <Box mt={3}>
                            {validationErrors}
                        </Box>
                    }

                    <Box my={4}>
                        <Button variant={'contained'} onClick={createSingleEvent} fullWidth>Add event</Button>
                    </Box>

                </Box>
            }
            { mode === 'nonSeries' &&
                <Box mt={4}>

                    <Stack mt={2}>
                        <AddEventWidget key={0} no={0} isFinal={false} isNonSeries={true} updateEventData={updateEventData} singleMode={true}/>
                    </Stack>

                    { validationErrors.length > 0 &&
                        <Box mt={3}>
                            {validationErrors}
                        </Box>
                    }

                    <Box my={4}>
                        <Button variant={'contained'} onClick={createNonSeriesEvent} fullWidth>Create event</Button>
                    </Box>

                </Box>
            }
            { mode === 'createConfirm' &&
                <CreateConfirmDialog events={createEventObjects} onClose={handleCreateConfirmDialogClose}/>
            }
            { mode ==='createSeries' &&
                <Box>
                    { createEventsResult.isSuccess &&
                        <Box>
                            <EventDetailsTable data={createEventsResult.data}/>
                        </Box>
                    }
                    { createEventsResult.isLoading &&
                        <Stack sx={{alignItems:'center'}}>
                            <Typography variant={'h3'} mb={4}>Creating events</Typography>
                            <CircularProgress />
                        </Stack>
                    }
                    <Box my={4}>
                        <Button variant={'contained'} onClick={handleCreateEventFinishedClick}>OK</Button>
                    </Box>
                </Box>
            }
        </Container>
    )
};

export default AddEvent;